import React, { useEffect, useState, useRef } from 'react';
import { Bell, MessageCircle, ChevronDown } from 'lucide-react';
import { SearchBar } from './SearchBar';
import { ProfileDropdown } from './ProfileDropdown';
import { NotificationsPanel } from './NotificationsPanel';
import { MessagesPanel } from './MessagesPanel';
import { User } from '../types';
interface NavigationProps {
  user: User;
  onCreateClick: () => void;
}
export function Navigation({
  user,
  onCreateClick
}: NavigationProps) {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isMessagesOpen, setIsMessagesOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'home' | 'create'>('home');
  const profileRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
      if (messagesRef.current && !messagesRef.current.contains(event.target as Node)) {
        setIsMessagesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleNotificationsClick = () => {
    setIsNotificationsOpen(!isNotificationsOpen);
    setIsMessagesOpen(false);
    setIsProfileOpen(false);
  };
  const handleMessagesClick = () => {
    setIsMessagesOpen(!isMessagesOpen);
    setIsNotificationsOpen(false);
    setIsProfileOpen(false);
  };
  const handleProfileClick = () => {
    setIsProfileOpen(!isProfileOpen);
    setIsNotificationsOpen(false);
    setIsMessagesOpen(false);
  };
  const handleHomeClick = () => {
    setActiveTab('home');
  };
  const handleCreateClick = () => {
    setActiveTab('create');
    onCreateClick();
  };
  return <nav className="sticky top-0 z-50 bg-white w-full h-[72px] flex items-center px-4 md:px-6 shadow-sm border-b border-gray-100" data-id="element-172">
      {/* Logo Wordmark */}
      <div className="flex items-center gap-2 mr-4 cursor-pointer" onClick={handleHomeClick} data-id="element-173">
        <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center text-white font-bold text-lg" data-id="element-174">
          P
        </div>
        <span className="text-lg font-bold tracking-tight hidden sm:block text-gray-900" data-id="element-175">
          Pinboard
        </span>
      </div>

      {/* Desktop Links */}
      <div className="hidden md:flex items-center gap-1 mr-4" data-id="element-176">
        <button onClick={handleHomeClick} className={`px-4 py-2 rounded-full font-medium text-sm transition-colors ${activeTab === 'home' ? 'bg-black text-white' : 'hover:bg-gray-100 text-gray-700'}`} data-id="element-177">
          Home
        </button>
        <button onClick={handleCreateClick} className={`px-4 py-2 rounded-full font-medium text-sm transition-colors ${activeTab === 'create' ? 'bg-black text-white' : 'hover:bg-gray-100 text-gray-700'}`} data-id="element-178">
          Create
        </button>
      </div>

      {/* Search */}
      <SearchBar data-id="element-179" />

      {/* Right Actions */}
      <div className="flex items-center gap-1 shrink-0 ml-2" data-id="element-180">
        {/* Notifications */}
        <div className="relative hidden sm:block" ref={notificationsRef} data-id="element-181">
          <button onClick={handleNotificationsClick} className={`p-2.5 rounded-full transition-colors ${isNotificationsOpen ? 'bg-black text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-black'}`} aria-label="Notifications" data-id="element-182">
            <Bell className="h-5 w-5" data-id="element-183" />
          </button>
          <NotificationsPanel isOpen={isNotificationsOpen} onClose={() => setIsNotificationsOpen(false)} data-id="element-184" />
        </div>

        {/* Messages */}
        <div className="relative hidden sm:block" ref={messagesRef} data-id="element-185">
          <button onClick={handleMessagesClick} className={`p-2.5 rounded-full transition-colors ${isMessagesOpen ? 'bg-black text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-black'}`} aria-label="Messages" data-id="element-186">
            <MessageCircle className="h-5 w-5" data-id="element-187" />
          </button>
          <MessagesPanel isOpen={isMessagesOpen} onClose={() => setIsMessagesOpen(false)} data-id="element-188" />
        </div>

        {/* Profile */}
        <div className="relative" ref={profileRef} data-id="element-189">
          <button onClick={handleProfileClick} className={`flex items-center gap-1 pl-1 pr-2 py-1 rounded-full transition-colors ml-1 ${isProfileOpen ? 'bg-gray-100' : 'hover:bg-gray-100'}`} aria-label="Profile" data-id="element-190">
            <div className="h-8 w-8 bg-gray-200 rounded-full overflow-hidden border border-gray-200" data-id="element-191">
              <img src={user.avatar} alt="User" className="h-full w-full object-cover" data-id="element-192" />
            </div>
            <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} data-id="element-193" />
          </button>

          <ProfileDropdown user={user} isOpen={isProfileOpen} onClose={() => setIsProfileOpen(false)} data-id="element-194" />
        </div>
      </div>
    </nav>;
}